import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import useAuth from '../../context/auth.context.jsx';
import useGroup from '../../context/group.context.jsx';
import groupServices from '../services/groupServices';
import Sheet from './common/sheet.jsx';
import Button from './common/button.jsx';
import Input from './common/input.jsx';
import ImportSplitwise from './models/importSplitwise.jsx';
import { useCurrencies } from '../hooks/useCurrencies.js';
import puzzleServices from '../services/puzzleServices.js';
import usePlanAccent from '../hooks/usePlanAccent.js';
import logo from '../assets/logo.png';

const LOCALE_CURRENCY = {
    'en-gb': 'GBP',
    'en-us': 'USD',
    'en-au': 'AUD',
    'en-ca': 'CAD',
    de: 'EUR',
    fr: 'EUR',
    es: 'EUR',
    it: 'EUR',
    nl: 'EUR',
    he: 'ILS',
    ru: 'RUB',
};

function suggestedCurrency() {
    const tag = String(typeof navigator !== 'undefined' ? navigator.language : '').toLowerCase();
    if (LOCALE_CURRENCY[tag]) return LOCALE_CURRENCY[tag];
    const lang = tag.split('-')[0];
    return LOCALE_CURRENCY[lang] || 'ILS';
}

function GroupTypeIcon({ type }) {
    return <i className={`bi ${type === 'GENERAL' ? 'bi-airplane' : 'bi-house-door'}`} aria-hidden="true" />;
}

// Used by the mobile app bar and the desktop sidebar. Carries the tour anchor —
// TourSpotlight finds the switcher step by [data-tour="group-pill"].
export function GroupPill({ onClick }) {
    const { t } = useTranslation('common');
    const { activeGroup } = useGroup();

    return (
        <button type="button" className="group-pill" data-tour="group-pill" onClick={onClick} aria-label={t('switchGroup')}>
            {activeGroup
                ? (
                    <>
                        {activeGroup.archivedAt
                            ? <i className="bi bi-archive" aria-hidden="true" title={t('archived')} />
                            : <GroupTypeIcon type={activeGroup.type} />}
                        <span>{activeGroup.name}</span>
                    </>
                )
                : <span>{t('selectGroup')}</span>}
            <i className="bi bi-chevron-down" style={{ fontSize: '0.8rem' }} />
        </button>
    );
}

// Archived groups live one tap below the switcher — visited rarely (a finished
// trip, once a year), so this is a quiet text link, never a main tab.
export function ArchiveSheet({ open, onClose }) {
    const { t } = useTranslation('common');
    const { userData } = useAuth();
    const { archivedGroups, setActiveGroup, refreshGroups } = useGroup();
    const [busyId, setBusyId] = useState(null);
    const me = userData?.user;

    const handleView = (group) => {
        setActiveGroup(group._id);
        onClose();
    };

    const handleUnarchive = async (group) => {
        if (!window.confirm(t('unarchiveConfirm', { name: group.name }))) return;
        setBusyId(group._id);
        try {
            await groupServices.unarchiveGroup(group._id);
            await refreshGroups();
            setActiveGroup(group._id);
        } catch (error) {
            toast.error(error.response?.data?.error || t('unarchiveFailed'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Sheet open={open} onClose={onClose} title={t('archiveTitle')}>
            {archivedGroups.length === 0 ? (
                <p className="text-muted-ms text-center my-3">
                    {t('archiveEmpty')}
                </p>
            ) : (
                archivedGroups.map((g) => {
                    const isManager = me && String(g.manager) === String(me._id);
                    return (
                        <div className="archive-row" key={g._id}>
                            <div className="expense-icon"><GroupTypeIcon type={g.type} /></div>
                            <button
                                type="button"
                                className="archive-row-name btn border-0 bg-transparent p-0 text-start-ms d-flex flex-column justify-content-center"
                                style={{ minHeight: 44 }}
                                onClick={() => handleView(g)}
                            >
                                <span className="archive-row-title">{g.name}</span>
                                <span className="archive-row-sub">{t('archiveReadonly')}</span>
                            </button>
                            {isManager && (
                                <div className="archive-row-actions">
                                    <button
                                        type="button"
                                        className="btn-ms btn-ms-outline btn-ms-sm"
                                        onClick={() => handleUnarchive(g)}
                                        disabled={busyId === g._id}
                                    >
                                        <i className="bi bi-box-arrow-up" /> {busyId === g._id ? t('unarchiving') : t('unarchive')}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </Sheet>
    );
}

export function GroupSwitcherSheet({ open, onClose }) {
    const { t } = useTranslation('common');
    const { myGroups, activeGroup, setActiveGroup, refreshGroups } = useGroup();
    const { currencies } = useCurrencies();
    const [mode, setMode] = useState(null); // null | 'create' | 'join'
    const [name, setName] = useState('');
    const [type, setType] = useState('APARTMENT');
    const [currency, setCurrency] = useState(suggestedCurrency);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    const close = () => { setMode(null); setName(''); setCode(''); setCurrency(suggestedCurrency()); onClose(); };

    const handleCreate = async () => {
        if (!name.trim()) return toast.error(t('groupNameRequired'));
        setBusy(true);
        try {
            const { group } = await groupServices.createGroup(name.trim(), type, currency);
            await refreshGroups();
            setActiveGroup(group._id);
            close();
        } catch (error) {
            toast.error(error.response?.data?.error || t('createGroupFailed'));
        } finally {
            setBusy(false);
        }
    };

    const handleJoin = async () => {
        if (!code.trim()) return toast.error(t('inviteCodeRequired'));
        setBusy(true);
        try {
            const { group } = await groupServices.joinByCode(code.trim());
            await refreshGroups();
            setActiveGroup(group._id);
            close();
        } catch (error) {
            toast.error(error.response?.data?.error || t('joinFailed'));
        } finally {
            setBusy(false);
        }
    };

    const sheetTitle = mode === 'create' ? t('newGroup') : mode === 'join' ? t('joinByCode') : t('myGroups');

    return (
        <>
        <Sheet open={open} onClose={close} title={sheetTitle}>
            {!mode && (
                <>
                    {myGroups.map(g => (
                        <button
                            key={g._id}
                            type="button"
                            className="btn-ms btn-ms-outline btn-ms-block mb-2 justify-content-between"
                            onClick={() => { setActiveGroup(g._id); close(); }}
                        >
                            <span className="d-flex align-items-center gap-2">
                                <GroupTypeIcon type={g.type} />
                                {g.name}
                            </span>
                            {activeGroup?._id === g._id && <i className="bi bi-check-lg text-success-ms" />}
                        </button>
                    ))}
                    {myGroups.length === 0 && (
                        <p className="text-muted-ms text-center my-3">{t('noGroupsYet')}</p>
                    )}
                    <div className="d-flex gap-2 mt-3">
                        <Button text={t('newGroup')} onClick={() => setMode('create')}>
                            <i className="bi bi-plus-lg" /> {t('newGroup')}
                        </Button>
                        <Button variant="outline" onClick={() => setMode('join')}>
                            <i className="bi bi-key" /> {t('joinByCode')}
                        </Button>
                    </div>
                    <button type="button" className="btn-ms btn-ms-link btn-ms-block mt-1" onClick={() => { close(); setImportOpen(true); }}>
                        <i className="bi bi-cloud-arrow-down" /> {t('importFromSplitwise')}
                    </button>
                    {/* quiet, deliberately not a main tab — visited maybe once a year */}
                    <button type="button" className="btn-ms btn-ms-link btn-ms-block mt-1" onClick={() => { close(); setArchiveOpen(true); }}>
                        <i className="bi bi-archive" /> {t('groupArchive')}
                    </button>
                </>
            )}

            {mode === 'create' && (
                <>
                    <Input label={t('groupName')} name="groupName" placeholder={t('groupNamePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
                    <label className="ms-label">{t('groupType')}</label>
                    <div className="category-chips mb-3">
                        <button type="button" className={`category-chip ${type === 'APARTMENT' ? 'selected' : ''}`} onClick={() => setType('APARTMENT')}>
                            <i className="bi bi-house-door" /> {t('groupTypeApartment')}
                        </button>
                        <button type="button" className={`category-chip ${type === 'GENERAL' ? 'selected' : ''}`} onClick={() => setType('GENERAL')}>
                            <i className="bi bi-airplane" /> {t('groupTypeTrip')}
                        </button>
                    </div>
                    <label className="ms-label" htmlFor="group-currency">{t('currency')}</label>
                    <select
                        id="group-currency"
                        className="ms-input mb-3"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                    >
                        {(currencies.some((c) => c.code === currency) ? currencies : [{ code: currency, name: currency, symbol: currency }, ...currencies]).map((c) => (
                            <option key={c.code} value={c.code}>
                                {c.symbol} {c.name} ({c.code})
                            </option>
                        ))}
                    </select>
                    <Button text={t('createGroup')} onClick={handleCreate} disabled={busy} />
                    <button type="button" className="btn-ms btn-ms-outline btn-ms-block mt-2" onClick={() => setMode(null)}>{t('back')}</button>
                </>
            )}

            {mode === 'join' && (
                <>
                    <Input label={t('inviteCode')} name="inviteCode" placeholder={t('inviteCodePlaceholder')} value={code} onChange={e => setCode(e.target.value)} style={{ textTransform: 'uppercase', letterSpacing: '2px' }} />
                    <Button text={t('join')} onClick={handleJoin} disabled={busy} />
                    <button type="button" className="btn-ms btn-ms-outline btn-ms-block mt-2" onClick={() => setMode(null)}>{t('back')}</button>
                </>
            )}
        </Sheet>
        <ArchiveSheet open={archiveOpen} onClose={() => setArchiveOpen(false)} />
        <ImportSplitwise open={importOpen} onClose={() => setImportOpen(false)} />
        </>
    );
}

// Entry point to the games hub. It lives in the app bar because the bottom nav
// is already at its five-item ceiling, and because that is where this pattern
// is recognised from elsewhere.
function GamesEntry() {
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [unplayed, setUnplayed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // One request per shell mount, not per page — the AppBar outlives routes.
        // The dot means "there is a stage you have not played", so any unplayed
        // game lights it; a dot per game would be a scoreboard in the chrome.
        puzzleServices.getStatus()
            .then(data => {
                if (cancelled) return;
                setUnplayed(Object.values(data.games || {}).some(g => !g.solved));
            })
            .catch(() => { /* the dot is a nicety; a failure just means no dot */ });
        return () => { cancelled = true; };
    }, []);

    return (
        <button
            type="button"
            className="games-entry games-scope"
            data-tour="games-entry"
            onClick={() => navigate('/games')}
            aria-label={unplayed ? t('gamesUnplayed') : t('games')}
        >
            <i className="bi bi-puzzle-fill" aria-hidden="true" />
            {unplayed && <span className="games-entry-dot" aria-hidden="true" />}
        </button>
    );
}

export default function AppBar() {
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const planAccent = usePlanAccent();

    return (
        <>
            {/* mobile app bar — desktop vs mobile is now decided in JS by
                ProtectedShell (useIsDesktop, 1024px); this no longer needs a
                Bootstrap breakpoint class, which was tied to 768px and would
                blank the header out between 768-1023px. */}
            <header className="app-bar">
                <span className="brand-logo-wrap">
                    <img src={logo} alt="Master Splitter" width="32" height="32" style={{ borderRadius: 8 }} />
                    {planAccent && <span className={`brand-logo-dot ${planAccent}`} aria-hidden="true" />}
                </span>
                <GroupPill onClick={() => setSwitcherOpen(true)} />
                <GamesEntry />
            </header>

            <GroupSwitcherSheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
        </>
    );
}
